import { IAuditLog } from "../../../../@types/AuditLog";
import { useAuditLogDetail } from "../../../../hooks/queries/admin/auditLogs";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import {
  ModalCloseButton,
  ModalContainer,
  ModalLabel,
  ModalSection,
  ModalTitle,
  ModalValue,
} from "../AdminPage.styles";
import { OperationBadge } from "./AuditLog.styles";
import { describeAuditAction } from "./auditLogAction";
import { AuditLogChangeSummary } from "./AuditLogChangeSummary";

interface IAuditLogDetailModalProps {
  auditLog: IAuditLog;
  /** The row came from a list without its before/after snapshots: fetch them. */
  loadDetails?: boolean;
  onClose: () => void;
}

type BadgeOperation = "INSERT" | "UPDATE" | "DELETE" | "OTHER";

function resolveBadgeOperation(operation: string | undefined): BadgeOperation {
  const normalized = operation?.toUpperCase();
  if (
    normalized === "INSERT" ||
    normalized === "UPDATE" ||
    normalized === "DELETE"
  ) {
    return normalized;
  }
  return "OTHER";
}

/**
 * Row drill-down shared by the standalone Audit Logs page and the embeddable
 * AuditLogPanel — the portal equivalent of Retool's `modalAuditLog` /
 * `modalAuditLogDetails` (same fields, plus the "Action" summary and a
 * readable diff instead of a raw JSON explorer).
 */
export function AuditLogDetailModal({
  auditLog,
  loadDetails = false,
  onClose,
}: IAuditLogDetailModalProps) {
  const details = useAuditLogDetail(auditLog.id, loadDetails);
  const badgeOperation = resolveBadgeOperation(auditLog.operation);

  return (
    <ModalContainer>
      <ModalTitle>Audit log details</ModalTitle>

      <ModalSection>
        <ModalLabel>Operation</ModalLabel>
        <OperationBadge operation={badgeOperation}>
          {auditLog.operation}
        </OperationBadge>
      </ModalSection>

      <ModalSection>
        <ModalLabel>Action</ModalLabel>
        <ModalValue>{describeAuditAction(auditLog)}</ModalValue>
      </ModalSection>

      <ModalSection>
        <ModalLabel>Table Name</ModalLabel>
        <ModalValue>{auditLog.tableName}</ModalValue>
      </ModalSection>

      <ModalSection>
        <ModalLabel>Date</ModalLabel>
        <ModalValue>
          {auditLog.timestamp ? getLongFormattedDate(auditLog.timestamp) : "-"}
        </ModalValue>
      </ModalSection>

      {auditLog.applicationUserEmail && (
        <ModalSection>
          <ModalLabel>User</ModalLabel>
          <ModalValue>{auditLog.applicationUserEmail}</ModalValue>
        </ModalSection>
      )}

      {auditLog.applicationUserId && (
        <ModalSection>
          <ModalLabel>User ID</ModalLabel>
          <ModalValue>{auditLog.applicationUserId}</ModalValue>
        </ModalSection>
      )}

      {auditLog.source && (
        <ModalSection>
          <ModalLabel>Source</ModalLabel>
          <ModalValue>{auditLog.source}</ModalValue>
        </ModalSection>
      )}

      {auditLog.entityId && (
        <ModalSection>
          <ModalLabel>Record ID</ModalLabel>
          <ModalValue>{auditLog.entityId}</ModalValue>
        </ModalSection>
      )}

      <ModalSection>
        <ModalLabel>Changes</ModalLabel>
        {loadDetails && details.isLoading && (
          <ModalValue>Loading changes...</ModalValue>
        )}
        {loadDetails && details.isError && (
          <ModalValue>Failed to load the changes. Try again.</ModalValue>
        )}
        {(!loadDetails || details.auditLog) && (
          <AuditLogChangeSummary auditLog={details.auditLog ?? auditLog} />
        )}
      </ModalSection>

      <ModalSection>
        <ModalLabel>Audit Log ID</ModalLabel>
        <ModalValue>{auditLog.id}</ModalValue>
      </ModalSection>

      <ModalCloseButton onClick={onClose}>Close</ModalCloseButton>
    </ModalContainer>
  );
}
